import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  normalizeStayPlanAdjustment,
  stayPlanAdjustmentSelect,
  type StayPlanAdjustment,
} from "@/hooks/useStayPlanAdjustments";
import {
  STAY_PLAN_TABLES,
  type RecurringUsageRule,
  type StayPlanKind,
  type StayPlanModule,
} from "@/lib/daycare-hotel/stayPlans";

export type StayPlanUsageSource = "hold" | "checkin";

export interface StayPlanUsage {
  id: string;
  usedOn: string;
  units: number;
  stayReservationId: string;
  source: StayPlanUsageSource;
}

export interface StayPlanCharge {
  id: string;
  dueOn: string;
  periodStartsOn: string;
  saleId: string;
  saleStatus: string;
  amount: number;
}

export interface StayPlanContract {
  id: string;
  clinicId: string;
  module: StayPlanModule;
  petId: string;
  planId: string;
  ownerId: string | null;
  planName: string;
  kind: StayPlanKind;
  price: number;
  startsOn: string;
  status: string;
  remainingDays: number | null;
  includedDays: number | null;
  usageRule: RecurringUsageRule | null;
  usageLimit: number | null;
  carryoverUnits: number;
  carryoverExpiresOn: string | null;
  firstDueOn: string | null;
  nextDueOn: string | null;
  billingTiming: "start" | "end";
  /** Diárias não usadas acumulam para o próximo ciclo. Contratos antigos acumulam. */
  carryoverEnabled: boolean;
  /** Dias fixos da matrícula (0=domingo…6=sábado). Vazio = contrato pendente. */
  fixedWeekdays: number[];
  /** 2 = quinzenal (semana sim, semana não). Congelado no contrato; antigos valem 1. */
  weekInterval?: 1 | 2;
  /** Quantidade de dias definidos não bate com o contratado. */
  daysReviewNeeded: boolean;
  usages: StayPlanUsage[];
  charges: StayPlanCharge[];
  adjustments: StayPlanAdjustment[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContractRow = any;

export function normalizeStayPlanContract(row: ContractRow, module: StayPlanModule): StayPlanContract {
  const snapshot = row.terms_snapshot ?? {};
  return {
    id: row.id,
    clinicId: row.clinic_id,
    module,
    petId: row.pet_id,
    planId: row.plan_id,
    ownerId: row.owner_id ?? null,
    planName: snapshot.name ?? row.plan?.name ?? "",
    kind: row.plan?.kind ?? "day_package",
    price: Number(snapshot.price ?? row.plan?.price ?? 0),
    startsOn: row.starts_on,
    status: row.status,
    remainingDays: row.remaining_days,
    includedDays: snapshot.included_days ?? row.plan?.day_package?.included_days ?? null,
    usageRule: row.plan?.recurring_rule?.usage_rule ?? null,
    usageLimit: row.plan?.recurring_rule?.usage_limit ?? null,
    carryoverUnits: Number(row.carryover_units ?? 0),
    carryoverExpiresOn: row.carryover_expires_on ?? null,
    firstDueOn: row.first_due_on ?? null,
    nextDueOn: row.next_due_on ?? null,
    billingTiming: row.billing_timing === "end" ? "end" : "start",
    // Ausente no snapshot = comportamento histórico, que sempre acumulou.
    carryoverEnabled: snapshot.carryover_enabled !== false,
    // Só creche tem dias fixos; no hotel as colunas nem são pedidas.
    fixedWeekdays: ((row.contract_days ?? []) as Array<{ weekday: number }>)
      .map((day) => day.weekday)
      .sort((a, b) => a - b),
    weekInterval: Number(snapshot.week_interval ?? 1) === 2 ? 2 : 1,
    daysReviewNeeded: Boolean(row.days_review_needed),
    usages: [...(row.usages ?? [])]
      .sort((a, b) => b.used_on.localeCompare(a.used_on))
      .map((usage: ContractRow) => ({
        id: usage.id,
        usedOn: usage.used_on,
        units: Number(usage.units ?? 1),
        stayReservationId: usage.stay_reservation_id,
        source: (usage.source ?? "checkin") as StayPlanUsageSource,
      })),
    charges: (row.charges ?? []).map((charge: ContractRow) => ({
      id: charge.id,
      dueOn: charge.due_on,
      periodStartsOn: charge.period_starts_on,
      saleId: charge.sale_id,
      saleStatus: charge.sale?.status ?? "",
      amount: Number(charge.sale?.total ?? 0),
    })),
    adjustments: (row.adjustments ?? []).map(normalizeStayPlanAdjustment),
  };
}

/**
 * Três baldes, não dois: `paused` não é consumível (o check-in não pode achar
 * o plano) mas também não é histórico — o contrato segue aberto e o card
 * continua mostrando ele.
 */
export function partitionStayPlanContracts(contracts: StayPlanContract[]) {
  const hasBalance = (contract: StayPlanContract) =>
    contract.remainingDays === null || contract.remainingDays > 0;
  const isActive = (contract: StayPlanContract) => contract.status === "active" && hasBalance(contract);
  const isPaused = (contract: StayPlanContract) => contract.status === "paused";

  return {
    active: contracts.filter(isActive),
    paused: contracts.filter(isPaused),
    history: contracts.filter((contract) => !isActive(contract) && !isPaused(contract)),
  };
}

export const stayPlanContractsKey = (
  module: StayPlanModule,
  clinicId: string | null,
  petIds: string[],
): unknown[] => ["stay-plan-contracts", module, clinicId, [...petIds].sort()];

export function contractSelect(module: StayPlanModule) {
  const tables = STAY_PLAN_TABLES[module];
  // Dias fixos e aviso de revisão existem só na creche; pedi-los no hotel faria
  // o PostgREST devolver erro de coluna inexistente.
  const daycareOnly =
    module === "daycare" ? "days_review_needed, contract_days:daycare_plan_contract_days(weekday), " : "";
  return (
    "id, clinic_id, pet_id, owner_id, plan_id, starts_on, first_due_on, next_due_on, billing_timing, remaining_days, status, " +
    "carryover_units, carryover_expires_on, terms_snapshot, " +
    daycareOnly +
    `plan:${tables.plans}(name, kind, price, ` +
    `day_package:${tables.dayPackages}(included_days), ` +
    `recurring_rule:${tables.recurringRules}(usage_rule, usage_limit)), ` +
    `usages:${tables.usages}(id, used_on, units, stay_reservation_id, source), ` +
    `charges:${tables.charges}(id, due_on, period_starts_on, sale_id, sale:sales(status, total)), ` +
    `adjustments:${tables.adjustments}(${stayPlanAdjustmentSelect(module)})`
  );
}

export function useStayPlanContracts(
  module: StayPlanModule,
  clinicId: string | null,
  petIds: string[],
): UseQueryResult<StayPlanContract[]> {
  return useQuery<StayPlanContract[]>({
    queryKey: stayPlanContractsKey(module, clinicId, petIds),
    enabled: Boolean(clinicId) && petIds.length > 0,
    queryFn: async () => {
      if (!clinicId || petIds.length === 0) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from(STAY_PLAN_TABLES[module].contracts)
        .select(contractSelect(module))
        .eq("clinic_id", clinicId)
        .in("pet_id", petIds)
        .order("starts_on", { ascending: false });

      if (error) throw error;
      return ((data ?? []) as ContractRow[]).map((row) => normalizeStayPlanContract(row, module));
    },
  });
}
